import type {ApprovedPaymentDetail, PaymentMethodType, PaymentSdkDescriptor} from '@/Types/PaymentComponent'
import type RainforestApprovedDetail from '@/Types/RainforestApprovedDetail'

/**
 * Pure internals of usePaymentComponent (ENG-4089), split out so the
 * descriptor-driven SDK injection and Rainforest event normalization are
 * unit-testable without React.
 */

export interface AcceptedMethods {
    card: boolean
    ach: boolean
    applePay: boolean
    paypal: boolean
    venmo: boolean
}

/**
 * The `allowed-methods` attribute for <rainforest-payment>. Order matters to
 * Rainforest — the first method is the widget's initially selected tab — so
 * `preferredMethod` moves that method to the front (PersonUpdatePaymentMethod
 * preselects the method of the payer's current schedule option).
 */
export function allowedMethodsAttribute(
    accepted: AcceptedMethods,
    preferredMethod?: 'CARD' | 'ACH',
): string {
    const methods: string[] = []

    if (accepted.card) methods.push('CARD')
    if (accepted.ach) methods.push('ACH')
    if (accepted.applePay) methods.push('APPLE_PAY')
    if (accepted.paypal) methods.push('PAYPAL_WALLET')
    if (accepted.venmo) methods.push('VENMO')

    if (preferredMethod != null && methods.includes(preferredMethod)) {
        methods.splice(methods.indexOf(preferredMethod), 1)
        methods.unshift(preferredMethod)
    }

    return methods.join(',')
}

/**
 * The verbatim payload of a Rainforest widget event: `event.detail[0].data`.
 */
function rainforestEventData<T>(event: Event): T | undefined {
    const detail = (event as CustomEvent).detail as Array<{data?: T}> | undefined

    return detail?.[0]?.data
}

/**
 * Normalizes a Rainforest `approved` event into the processor-neutral
 * ApprovedPaymentDetail, or null when the event carries no payload. This is
 * the only place application code may touch the verbatim payin payload —
 * everything after it reads the normalized fields, never `raw`.
 */
export function normalizeRainforestApproved(event: Event): ApprovedPaymentDetail | null {
    const data = rainforestEventData<RainforestApprovedDetail>(event)

    if (data == null) {
        return null
    }

    return {
        processor: 'rainforest',
        chargeId: data.payin_id,
        paymentMethodId: data.payment_method_id,
        methodType: data.method_type,
        accountLast4: data.card?.last_4 ?? data.ach?.account_number_last_4 ?? null,
        raw: data,
    }
}

/**
 * A human-readable decline reason from a Rainforest `declined` event, or
 * null when the widget did not report one.
 */
export function rainforestDeclineReason(event: Event): string | null {
    const data = rainforestEventData<RainforestApprovedDetail>(event)

    return data?.refusal_desc ?? data?.refusal_code ?? null
}

/**
 * The method type carried by a Rainforest `method-updated` event
 * (`event.detail[0]` is the bare method string), or null when absent.
 */
export function rainforestUpdatedMethodType(event: Event): PaymentMethodType | null {
    const detail = (event as CustomEvent).detail as PaymentMethodType[] | undefined

    return detail?.[0] ?? null
}

/**
 * Lazily injects the processor's browser SDK (a module <script>) the first
 * time a payment component mounts, replacing the hardcoded tags formerly in
 * app.blade.php / payments/mobile.blade.php. Dedupes purely against the
 * DOM — repeated mounts, other hook instances, and a tag already present in
 * the page all short-circuit — so there is no hidden module state.
 */
export function ensurePaymentSdk(sdkUrl: string, doc: Document = document): void {
    const existing = doc.querySelector(`script[src="${sdkUrl.replace(/"/g, '\\"')}"]`)

    if (existing != null) {
        return
    }

    const script = doc.createElement('script')
    script.type = 'module'
    script.src = sdkUrl

    doc.head.appendChild(script)
}

/**
 * Loads whichever browser SDK a processor needs, given a page's
 * `payment_sdk` prop or a full descriptor. Rainforest's payment.js is
 * injected (once); Stripe needs nothing, because `@stripe/stripe-js`
 * injects Stripe.js when it is imported and loadStripe() is the only
 * supported way to get a {stripeAccount}-scoped instance (ENG-4688).
 */
export function preloadPaymentSdk(sdk: PaymentSdkDescriptor | null | undefined, doc: Document = document): void {
    if (sdk?.processor === 'rainforest') {
        ensurePaymentSdk(sdk.sdkUrl, doc)
    }
}

// ---------------------------------------------------------------------------
// Stripe (ENG-4125). The same normalization job as the Rainforest helpers
// above, against Elements' shapes instead of the widget's CustomEvents.
// ---------------------------------------------------------------------------

/**
 * Stripe's payment method type ids for the neutral AcceptedMethods flags.
 * Venmo has no Stripe payment method type — it is reachable only inside
 * PayPal's own flow — so the flag has nothing to map to and is dropped.
 */
const STRIPE_METHOD_IDS: Record<Exclude<keyof AcceptedMethods, 'venmo'>, string> = {
    card: 'card',
    ach: 'us_bank_account',
    applePay: 'apple_pay',
    paypal: 'paypal',
}

/** The neutral method type behind each Stripe payment method type id. */
const NEUTRAL_METHOD_TYPES: Record<string, PaymentMethodType> = {
    card: 'CARD',
    us_bank_account: 'ACH',
    paypal: 'PAYPAL_WALLET',
}

/**
 * PaymentElement's `paymentMethodOrder` for the accepted set — the Stripe
 * analogue of `allowed-methods`, with one deliberate difference: it orders
 * the tabs, it does not gate them. What Elements actually offers is fixed
 * server-side by the PaymentIntent's `automatic_payment_methods`, which is
 * the only restriction Stripe honors; a client-side list is a display
 * preference the payer's browser could ignore.
 *
 * Undefined when no accepted set was passed, so the Stripe branch renders
 * PaymentElement with no ordering override at all — matching the Rainforest
 * branch's "omit the attribute entirely" default.
 */
export function stripePaymentMethodOrder(
    accepted?: AcceptedMethods,
    preferredMethod?: 'CARD' | 'ACH',
): string[] | undefined {
    if (accepted == null) {
        return undefined
    }

    const order = (Object.keys(STRIPE_METHOD_IDS) as Array<keyof typeof STRIPE_METHOD_IDS>)
        .filter((method) => accepted[method])
        .map((method) => STRIPE_METHOD_IDS[method])

    const preferredId = preferredMethod === 'CARD'
        ? STRIPE_METHOD_IDS.card
        : preferredMethod === 'ACH' ? STRIPE_METHOD_IDS.ach : null

    if (preferredId != null && order.includes(preferredId)) {
        order.splice(order.indexOf(preferredId), 1)
        order.unshift(preferredId)
    }

    /**
     * An empty list is dropped rather than forwarded. Rainforest reads
     * `allowed-methods=""` as "offer nothing" — PersonUpdatePaymentMethod
     * uses exactly that to gate capture until a schedule option is picked —
     * but `paymentMethodOrder: []` is not the Stripe spelling of that gate,
     * and handing Elements an empty array risks failing the mount outright.
     * Callers needing a real gate must withhold the descriptor instead.
     */
    return order.length > 0 ? order : undefined
}

export interface StripePaymentElementOptions {
    paymentMethodOrder?: string[]
    wallets?: {applePay: 'auto' | 'never', googlePay: 'auto' | 'never'}
}

/**
 * PaymentElement's options for the accepted set.
 *
 * The two halves of the set are gated at different layers, which is why
 * this is not simply `allowed-methods` translated:
 *
 * - card vs bank is an intent-level decision (`payment_method_types`, set
 *   server-side in Stripe\Concerns\PreparesCharges); all that is left here
 *   is tab order.
 * - Apple Pay and Google Pay are NOT Stripe payment method types — they are
 *   wallets riding on `card`, so the intent cannot exclude them and
 *   `wallets` is the only lever. That matters most on the POS iPad, where
 *   an agent tapping their own watch would charge the agent for a
 *   customer's ticket (MobilePaymentsController says as much).
 *
 * Google Pay follows the Apple Pay flag: the neutral AcceptedMethods has no
 * separate switch, and the reason to suppress one applies equally to the
 * other.
 *
 * Undefined when no accepted set was passed, leaving Elements' own defaults
 * — matching the Rainforest branch's "omit the attribute entirely".
 */
export function stripePaymentElementOptions(
    accepted?: AcceptedMethods,
    preferredMethod?: 'CARD' | 'ACH',
): StripePaymentElementOptions | undefined {
    if (accepted == null) {
        return undefined
    }

    const wallets = {
        applePay: accepted.applePay ? 'auto' : 'never',
        googlePay: accepted.applePay ? 'auto' : 'never',
    } as const

    const paymentMethodOrder = stripePaymentMethodOrder(accepted, preferredMethod)

    return paymentMethodOrder == null ? {wallets} : {paymentMethodOrder, wallets}
}

/**
 * The neutral method type for a PaymentElement `change` event's
 * `value.type`, or null for a Stripe method this app has no neutral name
 * for (callers only ever branch on CARD and ACH).
 */
export function stripeMethodType(stripeType: string | undefined): PaymentMethodType | null {
    return stripeType == null ? null : NEUTRAL_METHOD_TYPES[stripeType] ?? null
}

/**
 * Which confirm call the descriptor's intent needs. The card-entry flow
 * (`setup_only`) mints a SetupIntent, and `confirmPayment()` rejects one —
 * it has to be confirmed with `confirmSetup()`. Stripe encodes the kind in
 * the id prefix and the descriptor already carries the id, so nothing extra
 * has to be threaded through the seam to tell the two apart.
 */
export function stripeIntentKind(intentId: string): 'setup' | 'payment' {
    return intentId.startsWith('seti_') ? 'setup' : 'payment'
}

/**
 * The intent statuses that count as an approval. `processing` is not a
 * pending state to keep waiting on: ACH debits settle asynchronously and
 * never reach `succeeded` client-side, so treating it as anything but
 * approved would hang every bank payment (design doc §10.3).
 */
const STRIPE_APPROVED_STATUSES = ['succeeded', 'processing']

export function isStripeApproved(status: string | undefined): boolean {
    return status != null && STRIPE_APPROVED_STATUSES.includes(status)
}

/**
 * The subset of Stripe's PaymentIntent/SetupIntent this app reads. Declared
 * structurally rather than imported so the normalization stays testable
 * without constructing a full Stripe intent.
 */
export interface StripeConfirmedIntent {
    id: string
    status?: string
    payment_method?: string | {
        id: string
        card?: {last4?: string | null} | null
        us_bank_account?: {last4?: string | null} | null
    } | null
}

/**
 * The confirmed intent, normalized to the processor-neutral approval shape.
 *
 * Deliberately thin (design doc §10.3): under `redirect: 'if_required'`
 * Stripe returns `payment_method` as a bare id rather than an expanded
 * object, so there is no last4 to report and `methodType` comes from the
 * last `change` event rather than from the intent. The backend re-fetches
 * the intent to finalize and never trusts these fields — the ids are what
 * matter.
 */
export function normalizeStripeApproved(
    intent: StripeConfirmedIntent,
    methodType: PaymentMethodType,
): ApprovedPaymentDetail {
    const paymentMethod = intent.payment_method
    const expanded = typeof paymentMethod === 'string' ? null : paymentMethod

    return {
        processor: 'stripe',
        chargeId: intent.id,
        paymentMethodId: typeof paymentMethod === 'string'
            ? paymentMethod
            : expanded?.id ?? '',
        methodType,
        accountLast4: expanded?.card?.last4 ?? expanded?.us_bank_account?.last4 ?? null,
        raw: intent,
    }
}
